/**
 * Shared types and constants for User Management components
 */

/** Maximum number of role badges to show before truncating */
export const MAX_VISIBLE_BADGES = 2;

/** Role summary for display */
export interface RoleSummary {
	id: string;
	name: string;
	slug: string;
}

/** Team membership with role */
export interface TeamMembership {
	id: string;
	name: string;
	role: RoleSummary;
}

/**
 * Account user from listAccountUsers API
 * Users have one account role and zero or more team memberships
 */
export interface AccountUser {
	id: string;
	email: string;
	name: string | null;
	role: "user" | "admin" | "super_admin";
	createdAt: Date;
	accountRole: RoleSummary;
	teams: TeamMembership[];
}

/**
 * Account invite from listInvites API
 * Invites specify account role and optional team assignments
 */
export interface AccountInvite {
	id: string;
	token: string;
	email: string;
	accountRoleId: string;
	teamIds: string[];
	teamRoleId: string | null;
	expiresAt: Date;
	acceptedAt: Date | null;
	createdAt: Date;
	accountRole: RoleSummary | null;
	teamRole: RoleSummary | null;
	createdBy: {
		id: string;
		name: string | null;
		email: string;
	};
}

/**
 * Unified member type for the combined users/invites table
 * Following GCP's IAM pattern: one table with status column
 */
export type UnifiedMember =
	| {
			type: "invite";
			id: string;
			email: string;
			name: null;
			status: "Invited";
			accountRole: RoleSummary | null;
			teamRole: RoleSummary | null;
			teamIds: string[];
			teams: never[];
			createdAt: Date;
			expiresAt: Date;
			token: string;
	  }
	| {
			type: "user";
			id: string;
			email: string;
			name: string | null;
			status: "Active";
			accountRole: RoleSummary;
			teamRole: never;
			teamIds: never[];
			teams: TeamMembership[];
			createdAt: never;
			expiresAt: never;
			token: never;
	  };

/**
 * Role with capabilities from roles.list API
 */
export interface RoleWithCapabilities {
	id: string;
	name: string;
	slug: string;
	description: string | null;
	isSystem: boolean;
	capabilities: string[];
}

/**
 * Available capability from roles.listCapabilities API
 */
export interface AvailableCapability {
	capability: string;
	description: string;
	category: string;
}
